import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import PaidOutlined from "@mui/icons-material/PaidOutlined";
import AddRounded from "@mui/icons-material/AddRounded";
import EditOutlined from "@mui/icons-material/EditOutlined";
import RefreshRounded from "@mui/icons-material/RefreshRounded";
import SearchRounded from "@mui/icons-material/Search";
import { EmptyState, PageHeader, PageSkeleton } from "../components";
import CustomSnackbar, {
  CustomSnackbarRef,
} from "../components/CustomSnackbar";
import { useMedia } from "../context/MediaContext";
import { invalidateCurrencyOptions } from "../hooks/useCurrencyOptions";
import {
  createCurrency,
  CurrencyItem,
  CurrencyPayload,
  getCurrencies,
  reloadCurrencies,
  updateCurrency,
} from "../utils/api";

/**
 * Тот же шаблон, что проверяет бэкенд. Подчёркивание запрещено намеренно: в
 * базе турецкая лира записана как `try_`, и без запрета можно было бы завести
 * вторую валюту, неотличимую от неё.
 */
const CODE_PATTERN = /^[a-z][a-z0-9]{1,15}$/;

/**
 * Длина знака — как в колонке базы. Шаблона здесь нет намеренно: знак бывает
 * и `₼`, и `Bs.`, и арабской вязью, и перечислить допустимое значило бы
 * запретить половину настоящих знаков. Бэкенд проверяет то же самое.
 */
const SYMBOL_MAX_LEN = 8;

/** Управляющие символы попадают в поле только вставкой и ломают вёрстку суммы. */
// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = /[\u0000-\u001F\u007F]/;

/**
 * Знак не задан, если бэкенд вернул вместо него код заглавными: пустым это
 * поле не приходит — пустое место рядом с суммой хуже, чем «AED».
 */
const symbolIsSet = (item: CurrencyItem) =>
  item.symbol !== item.code.toUpperCase();

const errorText = (err: any, fallback: string): string => {
  const detail = err?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail[0]?.msg ?? fallback;
  return detail.message ?? fallback;
};

type FormState = {
  code: string;
  title: string;
  symbol: string;
  minor_units: string;
  api_alias: string;
};

const EMPTY_FORM: FormState = {
  code: "",
  title: "",
  symbol: "",
  minor_units: "2",
  api_alias: "",
};

const CurrenciesPage = () => {
  const theme = useTheme();
  const { isMobile600 } = useMedia();
  const snackbarRef = useRef<CustomSnackbarRef>(null);

  const [items, setItems] = useState<CurrencyItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [search, setSearch] = useState("");

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<CurrencyItem | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getCurrencies();
      setItems(res.data?.items ?? []);
      setFailed(false);
    } catch (err) {
      setFailed(true);
      snackbarRef.current?.showSnackbar(
        errorText(err, "Не удалось загрузить валюты"),
        "error",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const shown = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return items;
    return items.filter(
      (item) =>
        item.code.toLowerCase().includes(query) ||
        (item.title ?? "").toLowerCase().includes(query),
    );
  }, [items, search]);

  /**
   * Как будет выглядеть сумма. Знак и разрядность — два отдельных поля, и
   * вместе они дают то, что увидит плательщик; по отдельности это не читается.
   */
  const amountPreview = useMemo(() => {
    const minor = Number(form.minor_units);
    const digits =
      Number.isInteger(minor) && minor >= 0 && minor <= 4 ? minor : 2;
    const amount = (1234.5).toLocaleString("ru-RU", {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    });
    // Пустое поле знака бэкенд заменит кодом заглавными — так и показываем.
    const sign =
      form.symbol.trim() || (editing?.code ?? form.code).trim().toUpperCase();
    return sign ? `${amount} ${sign}` : amount;
  }, [form.symbol, form.minor_units, form.code, editing]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setFormError(null);
    setFormOpen(true);
  };

  const openEdit = (item: CurrencyItem) => {
    setEditing(item);
    setForm({
      code: item.code,
      title: item.title ?? "",
      // Подставленный бэкенд code.toUpperCase() в поле не кладём: иначе
      // «знак не задан» выглядело бы как введённый знак, и первое же
      // сохранение записало бы «AED» знаком всерьёз.
      symbol: symbolIsSet(item) ? item.symbol : "",
      minor_units: String(item.minor_units),
      api_alias: item.api_alias ?? "",
    });
    setFormError(null);
    setFormOpen(true);
  };

  const submit = async () => {
    const code = form.code.trim().toLowerCase();
    if (!editing && !CODE_PATTERN.test(code)) {
      setFormError(
        "Код: строчная латиница и цифры, начинается с буквы, 2–16 символов, без подчёркивания",
      );
      return;
    }

    const alias = form.api_alias.trim().toLowerCase();
    if (alias && !CODE_PATTERN.test(alias)) {
      setFormError("Алиас проверяется по тем же правилам, что и код");
      return;
    }

    const symbol = form.symbol.trim();
    if (symbol.length > SYMBOL_MAX_LEN) {
      setFormError(`Знак валюты — не длиннее ${SYMBOL_MAX_LEN} символов`);
      return;
    }
    if (CONTROL_CHARS.test(symbol)) {
      setFormError("Знак валюты: управляющие символы недопустимы");
      return;
    }

    const minor = Number(form.minor_units);
    if (!Number.isInteger(minor) || minor < 0 || minor > 4) {
      setFormError("Знаков после запятой — целое от 0 до 4");
      return;
    }

    const payload: CurrencyPayload = {
      title: form.title.trim() || null,
      // Пустое поле бэкенд превращает в код заглавными — это и значит
      // «знака нет, показывать код».
      symbol: symbol || null,
      minor_units: minor,
      // Алиас, равный коду, бэкенд всё равно игнорирует.
      api_alias: alias && alias !== (editing?.code ?? code) ? alias : null,
    };

    setSaving(true);
    setFormError(null);
    try {
      if (editing) {
        const res = await updateCurrency(editing.code, payload);
        // Алиас виден мерчанту: правка меняет то, что он разбирает в ответах.
        if (res.data?.warning) {
          snackbarRef.current?.showSnackbar(res.data.warning, "warning");
        } else {
          snackbarRef.current?.showSnackbar("Валюта обновлена", "success");
        }
      } else {
        await createCurrency(code, payload);
        snackbarRef.current?.showSnackbar(
          "Валюта заведена. Чтобы по ней пошли заказы, заведите в ней метод оплаты",
          "success",
        );
      }
      setFormOpen(false);
      load();
      // Переключатель валют и знак рядом с суммой читают тот же реестр —
      // иначе правка была бы видна только после перезагрузки страницы.
      invalidateCurrencyOptions();
    } catch (err) {
      setFormError(errorText(err, "Не удалось сохранить"));
    } finally {
      setSaving(false);
    }
  };

  const reload = async () => {
    try {
      const res = await reloadCurrencies();
      snackbarRef.current?.showSnackbar(
        `Перечитано валют: ${res.data?.loaded ?? 0}, методов: ${
          res.data?.methods_reloaded ?? 0
        }`,
        "success",
      );
      load();
      invalidateCurrencyOptions();
    } catch (err) {
      snackbarRef.current?.showSnackbar(
        errorText(err, "Не удалось перечитать"),
        "error",
      );
    }
  };

  const sourceChip = (item: CurrencyItem) => (
    <Chip
      size="small"
      label={item.source}
      variant={item.source === "база" ? "filled" : "outlined"}
      color={item.source === "база" ? "primary" : "default"}
      sx={{ height: 20, fontSize: 11, fontWeight: 600 }}
    />
  );

  /**
   * Знак либо свой, либо подставленный код. Второе надо различать: знак стоит
   * рядом с суммой у плательщика, и «AED» вместо «د.إ» — это недозаполненная
   * валюта, а не оформление.
   */
  const symbolCell = (item: CurrencyItem) => {
    if (symbolIsSet(item)) {
      return (
        <Typography component="span" sx={{ fontSize: 16, fontWeight: 700 }}>
          {item.symbol}
        </Typography>
      );
    }
    return (
      <Tooltip title="Знак не задан — рядом с суммой показывается код. Задайте его в правке валюты">
        <Typography
          component="span"
          color="text.secondary"
          sx={{ fontSize: 13, fontWeight: 600 }}
        >
          {item.symbol}
        </Typography>
      </Tooltip>
    );
  };

  /**
   * Валюта без методов не влияет ни на один заказ. Это надо показывать: иначе
   * непонятно, что вообще изменит правка.
   */
  const methodsCell = (item: CurrencyItem) => {
    if (!item.methods.length) {
      return (
        <Tooltip title="По этой валюте не заведён ни один метод оплаты — заказы по ней не пойдут">
          <Chip
            size="small"
            color="warning"
            label="нет методов"
            sx={{ height: 20, fontSize: 11 }}
          />
        </Tooltip>
      );
    }
    return (
      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
        {item.methods.map((method) => (
          <Chip
            key={method}
            size="small"
            label={method}
            sx={{ height: 20, fontSize: 11, fontFamily: "monospace" }}
          />
        ))}
      </Stack>
    );
  };

  const renderContent = () => {
    if (loading) return <PageSkeleton />;

    if (failed) {
      return (
        <EmptyState
          icon={PaidOutlined}
          title="Реестр недоступен"
          description="Не удалось получить валюты. Раздел доступен только администратору."
          action={
            <Button variant="contained" onClick={load}>
              Повторить
            </Button>
          }
        />
      );
    }

    if (!shown.length) {
      return (
        <EmptyState
          icon={PaidOutlined}
          title="Валют не найдено"
          description={
            search ? "По запросу ничего нет." : "Заведите первую валюту."
          }
          action={
            <Button
              variant="contained"
              startIcon={<AddRounded />}
              onClick={openCreate}
            >
              Добавить
            </Button>
          }
        />
      );
    }

    if (isMobile600) {
      return (
        <Stack spacing={1.5}>
          {shown.map((item) => (
            <Paper
              key={item.code}
              variant="outlined"
              sx={{ p: 2, borderRadius: "16px" }}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="flex-start"
                spacing={1}
              >
                <Box minWidth={0}>
                  <Stack direction="row" spacing={0.75} alignItems="baseline">
                    <Typography sx={{ fontWeight: 700 }}>
                      {item.code.toUpperCase()}
                    </Typography>
                    {symbolIsSet(item) && (
                      <Typography
                        color="text.secondary"
                        sx={{ fontSize: 15, fontWeight: 700 }}
                      >
                        {item.symbol}
                      </Typography>
                    )}
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    {item.title ?? "без названия"} · знаков: {item.minor_units}
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={0.75}
                    sx={{ mt: 0.75 }}
                    alignItems="center"
                  >
                    {sourceChip(item)}
                    {item.api_alias && (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`наружу: ${item.api_alias}`}
                        sx={{ height: 20, fontSize: 11 }}
                      />
                    )}
                  </Stack>
                </Box>
                <IconButton
                  size="small"
                  aria-label={`Изменить ${item.code}`}
                  onClick={() => openEdit(item)}
                >
                  <EditOutlined fontSize="small" />
                </IconButton>
              </Stack>
              <Box sx={{ mt: 1.5 }}>{methodsCell(item)}</Box>
            </Paper>
          ))}
        </Stack>
      );
    }

    return (
      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{ borderRadius: "16px" }}
      >
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: theme.palette.table.head }}>
              <TableCell sx={{ fontWeight: 700 }}>Код</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Название</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Знак</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Знаков</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Наружу</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Методы</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Источник</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>
                Действия
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {shown.map((item) => (
              <TableRow key={item.code} hover>
                <TableCell sx={{ fontFamily: "monospace", fontWeight: 600 }}>
                  {item.code}
                </TableCell>
                <TableCell>{item.title ?? "—"}</TableCell>
                <TableCell>{symbolCell(item)}</TableCell>
                <TableCell>{item.minor_units}</TableCell>
                <TableCell sx={{ fontFamily: "monospace" }}>
                  {item.api_alias ?? "—"}
                </TableCell>
                <TableCell>{methodsCell(item)}</TableCell>
                <TableCell>{sourceChip(item)}</TableCell>
                <TableCell align="right">
                  <Tooltip title="Изменить">
                    <IconButton
                      size="small"
                      aria-label={`Изменить ${item.code}`}
                      onClick={() => openEdit(item)}
                    >
                      <EditOutlined fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Box
      sx={{
        width: "100%",
        maxHeight: {
          xs: "calc(var(--content-max-height) + 32.5dvh)",
          md: "82vh",
        },
        overflow: "auto",
        p: { xs: 2, sm: 3 },
      }}
    >
      <PageHeader
        icon={PaidOutlined}
        title="Валюты"
        description="Реестр валют: название, знак, разрядность и имя в ответах мерчанту"
        actions={
          <>
            <Tooltip title="Перечитать валюты и методы из базы. Нужно, только если их меняли напрямую в БД">
              <Button
                variant="outlined"
                startIcon={<RefreshRounded />}
                onClick={reload}
              >
                Перечитать
              </Button>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<AddRounded />}
              onClick={openCreate}
            >
              Добавить
            </Button>
          </>
        }
      />

      <TextField
        size="small"
        fullWidth
        placeholder="Поиск по коду или названию"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchRounded fontSize="small" />
            </InputAdornment>
          ),
        }}
      />

      {renderContent()}

      <Dialog
        open={formOpen}
        onClose={() => !saving && setFormOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {editing ? `Валюта ${editing.code}` : "Новая валюта"}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            {!editing ? (
              <>
                <TextField
                  label="Код валюты"
                  size="small"
                  fullWidth
                  value={form.code}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, code: e.target.value }))
                  }
                  helperText="Строчная латиница и цифры, без подчёркивания. Изменить потом нельзя."
                />
                <Alert severity="info" sx={{ borderRadius: "12px" }}>
                  Сама валюта ничего не включает. Чтобы по ней пошли заказы,
                  после создания заведите в ней метод оплаты, а по методу —
                  тариф мерчанта.
                </Alert>
              </>
            ) : (
              <Typography variant="caption" color="text.secondary">
                Код не меняется: он хранится в заявках и в типе базы. Валюту с
                другим кодом заводят заново.
              </Typography>
            )}

            <TextField
              label="Название"
              size="small"
              fullWidth
              value={form.title}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, title: e.target.value }))
              }
              helperText="Только для интерфейса, на ответы API не влияет"
            />

            <TextField
              label="Знак валюты"
              size="small"
              fullWidth
              value={form.symbol}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, symbol: e.target.value }))
              }
              inputProps={{ maxLength: SYMBOL_MAX_LEN }}
              helperText={`Стоит рядом с суммой: ₽, ₼, Bs. До ${SYMBOL_MAX_LEN} символов. Пусто — будет показан код заглавными`}
            />

            {/* Знак и разрядность вместе задают вид суммы, а задаются по
                отдельности — показываем, что из них выходит. */}
            <Typography variant="caption" color="text.secondary">
              Сумма будет выглядеть так: {amountPreview}
            </Typography>

            <TextField
              label="Знаков после запятой"
              size="small"
              type="number"
              fullWidth
              value={form.minor_units}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, minor_units: e.target.value }))
              }
              helperText="2 у большинства, 3 у динара, 0 у воны и донга"
            />

            <TextField
              label="Имя в ответах мерчанту"
              size="small"
              fullWidth
              value={form.api_alias}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, api_alias: e.target.value }))
              }
              helperText="Заполняется, только если наружу валюта зовётся иначе"
            />

            {form.api_alias.trim() && (
              <Alert severity="warning" sx={{ borderRadius: "12px" }}>
                Алиас виден мерчанту: под этим именем валюта уходит в ответах
                API и в колбэках. Правка молча меняет то, что он разбирает.
              </Alert>
            )}

            {formError && (
              <Typography variant="body2" color="error">
                {formError}
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setFormOpen(false)} disabled={saving}>
            Отмена
          </Button>
          <Button variant="contained" onClick={submit} disabled={saving}>
            {saving ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              "Сохранить"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <CustomSnackbar ref={snackbarRef} />
    </Box>
  );
};

export default CurrenciesPage;
